
import { useState } from 'react';
import { Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CarDetailsSection from './CarDetailsSection';
import { useAuth } from '@/hooks/useAuth';

interface OilRequestFormProps {
  onSubmit: (data: any) => Promise<void>;
}

export const OilRequestForm = ({ onSubmit }: OilRequestFormProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    customer_phone: '',
    customer_email: user?.email || '',
    car_make: '',
    car_model: '',
    car_year: '',
    engine_type: '',
    customer_message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_phone || !formData.car_make || 
        !formData.car_model || !formData.car_year || !formData.engine_type) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
      // Reset form
      setFormData({
        customer_name: user?.name || '',
        customer_phone: '',
        customer_email: user?.email || '',
        car_make: '',
        car_model: '',
        car_year: '',
        engine_type: '',
        customer_message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>طلب توصية زيت السيارة</CardTitle>
          <CardDescription>
            املأ البيانات التالية وسنقوم بالرد عليك بأفضل توصيات الزيوت لسيارتك
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">معلومات التواصل</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer_name">الاسم الكامل *</Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => updateFormData('customer_name', e.target.value)}
                    placeholder="أدخل اسمك الكامل"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="customer_phone">رقم الهاتف *</Label>
                  <Input
                    id="customer_phone"
                    value={formData.customer_phone}
                    onChange={(e) => updateFormData('customer_phone', e.target.value)}
                    placeholder="05xxxxxxxx"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="customer_email">البريد الإلكتروني</Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => updateFormData('customer_email', e.target.value)}
                  placeholder="name@example.com"
                />
              </div>
            </div>

            {/* Car Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">معلومات السيارة</h3>
              <CarDetailsSection 
                formData={{
                  carMake: formData.car_make,
                  carModel: formData.car_model,
                  carYear: formData.car_year,
                  engineType: formData.engine_type
                }}
                onInputChange={(field: string, value: string) => {
                  if (field === 'carMake') updateFormData('car_make', value);
                  else if (field === 'carModel') updateFormData('car_model', value);
                  else if (field === 'carYear') updateFormData('car_year', value);
                  else if (field === 'engineType') updateFormData('engine_type', value);
                }}
              />
            </div>

            {/* Additional Message */}
            <div>
              <Label htmlFor="customer_message">رسالة إضافية (اختياري)</Label>
              <Textarea
                id="customer_message"
                value={formData.customer_message}
                onChange={(e) => updateFormData('customer_message', e.target.value)}
                placeholder="أي معلومات إضافية أو أسئلة خاصة بسيارتك..."
                rows={4}
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              <Send className="w-4 h-4 mr-2" />
              {loading ? 'جاري الإرسال...' : 'إرسال الطلب'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
